import { GhettoStream, MuLawDecoder, PcmAudioPlayer } from 'ui3';
/* eslint-disable jsx-a11y/media-has-caption */
import { useCallback, useEffect, useRef, useState } from 'react';

import JMuxer from 'jmuxer';
import { processMultipartStream } from '@/app/main/subadmin/livestreaming/history/Salient/H264VideoPlayer/processMultipartStream';
import { processStream } from '@/app/main/subadmin/livestreaming/history/PBJ/H264VideoPlayer/processStream';

const SalientH264Player = ({ streamData, volume, onVolumeChange, showControls, setShowControls }) => {
	const playerRef = useRef(null);
	const jmuxerRef = useRef(null);
	const pcmPlayerRef = useRef(null);
	const videoAbortControllerRef = useRef(null);
	const audioAbortControllerRef = useRef(null);
	const isPausedRef = useRef(false);
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMuted, setIsMuted] = useState(false);
	const boundary = 'myboundary'; // Default boundary

	const callStreamEnded = useCallback((abortController) => {
		return (reader) => {
			if (abortController) {
				abortController.abort();
			}

			if (reader) {
				const cancelPromise = reader.cancel('Streaming canceled');
				if (cancelPromise && cancelPromise.catch) {
					cancelPromise.catch((e) => {
						if (e && (e.code === DOMException.ABORT_ERR || e.code === DOMException.INVALID_STATE_ERR)) {
							// Expected result. Suppress error.
						} else {
							console.error(e);
						}
					});
				}
				reader = null;
			}
		};
	}, []);

	// Video frame callback for multipart stream (simplified PBJ-style approach)
	const callVideoFrameCallback = useCallback((jmuxer, player) => {
		let init = false;
		let frameCount = 0;

		return (frame) => {
			// Handle H264 video frames - simplified approach like PBJ
			if (
				frame.contentType === 'image/jpeg' ||
				frame.contentType === 'video/h264' ||
				frame.contentType === 'application/octet-stream'
			) {
				if (jmuxer && frame.data) {
					try {
						// Feed the frame data to JMuxer (PBJ-style: no duration, no complex analysis)
						jmuxer.feed({
							video: frame.data,
						});

						frameCount++;

						// Start playing once we have some frames (like PBJ)
						if (!init && frameCount >= 2) {
							setTimeout(() => {
								const playPromise = player.play();
								if (playPromise !== undefined) {
									playPromise
										.then(() => {
											setIsPlaying(true);
											setIsLoading(false);
										})
										.catch((e) => {
											console.error('Playback error:', e);
											setIsLoading(false);
										});
								}
							}, 100);
							init = true;
						}
					} catch (error) {
						console.error('Error feeding frame data to JMuxer:', error);
					}
				}
			}
		};
	}, []);

	// Audio frame callback for PBJ-style audio stream
	const callAudioFrameCallback = useCallback((pcmPlayer, muLawDecoder) => {
		return (frame) => {
			if (frame.isAudio) {
				// The only supported format is mu-law encoding with one audio channel.
				if (frame.format.wFormatTag === 7 && frame.format.nChannels === 1) {
					// 7 is mu-law, mu-law is 8 bits per sample but decodes to 16 bits per sample.
					const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(frame.frameData);
					pcmPlayer.AcceptBuffer([pcm16Bit], frame.format.nSamplesPerSec);
				} else if (frame.format.wFormatTag === 61868) {
					pcmPlayer.DecodeAndPlayAudioData(frame.frameData, frame.format.nSamplesPerSec);
				} else {
					console.log('Unsupported audio frame format', frame.format, frame);
				}
			}
		};
	}, []);

	const fetchVideoStream = useCallback(
		(streamUrl, fetchArgs, player, abortController) => {
			// Initialize JMuxer
			if (jmuxerRef.current) {
				jmuxerRef.current.destroy();
				jmuxerRef.current = null;
			}

			// Use PBJ-style JMuxer configuration (known to work)
			const jmuxer = new JMuxer({
				node: player,
				mode: 'video',
				maxDelay: 1000,
				flushingTime: 1000,
				clearBuffer: true,
				debug: false,
				fps: 30,
			});

			jmuxer.cancelDelay = function () {
				if (this.node.buffered && this.node.buffered.length > 0 && !this.node.seeking && !this.node.paused) {
					const end = this.node.buffered.end(0);
					if (end - this.node.currentTime > this.options.maxDelay / 1000) {
						this.node.currentTime = end - 0.001;
					}
				}
			};

			jmuxerRef.current = jmuxer;

			const onEndStream = callStreamEnded(abortController);

			fetch(streamUrl, fetchArgs)
				.then((res) => {
					const contentType = res.headers.get('Content-Type');

					if (res.status === 200) {
						if (contentType && contentType.includes('multipart/x-mixed-replace')) {
							setError('');
							const reader = res.body.getReader();

							// Create frame callback
							const frameCallback = callVideoFrameCallback(jmuxerRef.current, player);

							// Extract boundary from content type if available
							const boundaryMatch = contentType.match(/boundary=([^;]+)/);
							const actualBoundary = boundaryMatch ? boundaryMatch[1] : boundary;

							// Start processing the multipart stream
							processMultipartStream(reader, actualBoundary, frameCallback, onEndStream, isPausedRef);
						}
					}
				})
				.catch((e) => {
					if (e.name === 'AbortError') {
						return;
					}
					setError('Video server is unreachable');
					setIsLoading(false);
					console.error('Video fetch error:', e);
					onEndStream();
				});
		},
		[callStreamEnded, callVideoFrameCallback, boundary]
	);

	const fetchAudioStream = useCallback(
		(audioUrl, abortController) => {
			if (pcmPlayerRef.current) {
				pcmPlayerRef.current.Reset();
			}

			const pcmPlayer = new PcmAudioPlayer();
			pcmPlayerRef.current = pcmPlayer;
			const muLawDecoder = new MuLawDecoder();
			pcmPlayer.SetAudioVolumeFromSettings(volume);

			try {
				const ws = new WebSocket(audioUrl);
				ws.binaryType = 'arraybuffer';

				ws.onmessage = (event) => {
					if (event.data instanceof ArrayBuffer) {
						const audioData = new Uint8Array(event.data);

						// Convert to string to check for multipart headers
						const dataString = new TextDecoder().decode(audioData);

						// Check if this is binary protocol data with headers
						if (dataString.includes('Content-Length') || dataString.includes('Content-Type')) {
							// Try to extract content length from binary data
							let contentLength = 160; // From the logs, we can see Content-Length is 160

							// Look for Content-Length pattern in string
							const contentLengthMatch = dataString.match(/Content-Length.(\d+)/);
							if (contentLengthMatch) {
								contentLength = parseInt(contentLengthMatch[1]);
							}

							// Find the actual audio content by looking for binary patterns
							// The data appears to be a binary protocol with interleaved headers and content
							let audioContent = null;

							// Method 1: Look for sequences that don't contain readable text
							for (let i = 0; i < audioData.length - contentLength; i++) {
								const candidate = audioData.slice(i, i + contentLength);
								const candidateString = new TextDecoder('utf-8', { fatal: false }).decode(candidate);

								// Check if this slice contains mostly non-text (likely audio)
								const textChars = candidateString
									.split('')
									.filter((c) => c.charCodeAt(0) >= 32 && c.charCodeAt(0) <= 126).length;
								const textRatio = textChars / candidateString.length;

								if (textRatio < 0.1) {
									// Less than 10% readable text
									audioContent = candidate;
									break;
								}
							}

							// Method 2: If no low-text content found, try looking for specific byte patterns
							if (!audioContent) {
								// Look for common mu-law audio patterns (values typically in mid-range)
								for (let i = 0; i < audioData.length - contentLength; i++) {
									const candidate = audioData.slice(i, i + contentLength);

									// Count bytes in typical mu-law range (avoiding extreme values)
									let muLawLikeBytes = 0;
									for (let j = 0; j < candidate.length; j++) {
										const byte = candidate[j];
										if (byte > 16 && byte < 240) {
											// Typical mu-law range
											muLawLikeBytes++;
										}
									}

									const muLawRatio = muLawLikeBytes / candidate.length;
									if (muLawRatio > 0.7) {
										// More than 70% in mu-law range
										audioContent = candidate;
										break;
									}
								}
							}

							// Method 3: If still no content, try extracting from the end, skipping header bytes
							if (!audioContent && audioData.length >= contentLength + 50) {
								// Skip potential header bytes and take content length
								audioContent = audioData.slice(audioData.length - contentLength - 10, audioData.length - 10);
							}

							if (audioContent) {
								// Try to decode as mu-law audio
								try {
									const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(audioContent);
									pcmPlayer.AcceptBuffer([pcm16Bit], 8000);
								} catch (decodeError) {
									console.error('Audio decode error from binary protocol:', decodeError);
								}
							} else {
								console.error('Could not extract audio content from binary protocol');
							}
						} else {
							// Direct audio data - try to decode as mu-law
							try {
								const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(audioData);
								pcmPlayer.AcceptBuffer([pcm16Bit], 8000);
							} catch (decodeError) {
								console.error('Direct audio decode error:', decodeError);
							}
						}
					}
				};

				ws.onerror = (error) => {
					console.error('WebSocket audio error:', error);
				};

				// Handle abort controller
				const abortHandler = () => {
					if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
						ws.close();
					}
				};

				if (abortController) {
					abortController.signal.addEventListener('abort', abortHandler);
				}

				return () => {
					if (abortController) {
						abortController.signal.removeEventListener('abort', abortHandler);
					}
					if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
						ws.close();
					}
				};
			} catch (error) {
				console.error('Failed to create WebSocket connection:', error);
				return null;
			}
		},
		[volume]
	);

	// Update volume when it changes
	useEffect(() => {
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(isMuted ? 0 : volume);
		}
	}, [volume, isMuted]);

	useEffect(() => {
		const videoAbortController = new AbortController();
		const audioAbortController = new AbortController();
		videoAbortControllerRef.current = videoAbortController;
		audioAbortControllerRef.current = audioAbortController;
		const videoElement = playerRef.current;

		if (videoElement && streamData?.session_url) {
			setIsLoading(true);
			const fetchArgs = { credentials: 'same-origin' };

			// Fetch video stream
			fetchVideoStream(
				streamData.session_url,
				{ ...fetchArgs, signal: videoAbortController.signal },
				videoElement,
				videoAbortController
			);

			// Fetch audio stream if available
			if (streamData.audio_url) {
        // TODO: Uncomment this when we have audio available.
				//fetchAudioStream(streamData.audio_url, audioAbortController);
			}

			return () => {
				if (pcmPlayerRef.current) {
					pcmPlayerRef.current.Reset();
				}
				if (videoAbortControllerRef.current) {
					videoAbortControllerRef.current.abort();
				}
				if (audioAbortControllerRef.current) {
					audioAbortControllerRef.current.abort();
				}
			};
		}
	}, [streamData?.session_url, streamData?.audio_url]);

	const handlePlayPause = () => {
		const videoElement = playerRef.current;
		if (videoElement) {
			if (isPlaying) {
				videoElement.pause();
				isPausedRef.current = true;
				setIsPlaying(false);
				pcmPlayerRef.current?.Pause();
			} else {
				videoElement.play();
				isPausedRef.current = false;
				setIsPlaying(true);
				pcmPlayerRef.current?.Resume();
			}
		}
	};

	const handleMute = () => {
		setIsMuted(!isMuted);
	};

	const handleVolumeChange = (newVolume) => {
		onVolumeChange(newVolume);
	};

	return (
		<div className="relative w-full h-full">
			{error ? (
				<div className="absolute inset-0 flex items-center justify-center bg-black text-white">
					<h3 className="text-red-500">{error}</h3>
				</div>
			) : null}

			{isLoading ? (
				<div className="absolute inset-0 flex items-center justify-center bg-black text-white">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
				</div>
			) : null}

			<video
				className="w-full h-full object-cover"
				ref={playerRef}
				crossOrigin="anonymous"
				playsInline
				autoPlay
				muted
				onMouseEnter={() => setShowControls(true)}
				onMouseLeave={() => setShowControls(false)}
			/>

			{showControls ? (
				<div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-75 rounded p-2 flex items-center justify-between">
					<div className="flex items-center space-x-2">
						<button className="text-white hover:text-gray-300" onClick={handlePlayPause} type="button">
							{isPlaying ? '⏸️' : '▶️'}
						</button>

						<button className="text-white hover:text-gray-300" onClick={handleMute} type="button">
							{isMuted ? '🔇' : '🔊'}
						</button>

						<input
							type="range"
							min="0"
							max="1"
							step="0.1"
							value={volume}
							onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
							className="w-20"
						/>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default SalientH264Player;
